import { ChangeEvent } from "react";
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Collapse,
  FormGroup,
  CircularProgress,
} from "@mui/material";
import { CheckValidationIcon } from "../../../../../../assets/JWayIcons";
import {
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  ExpandMore,
} from "@mui/icons-material";

import { SpendingDemandStatus } from "../../../../../spending/types/spending.types";
import { SpendingDemandTypes } from "../../../../types/spendingDemand.types";


export interface StatusItem {
  value: SpendingDemandStatus;
  label: string;
}

export interface TypeItem {
  value: SpendingDemandTypes;
  label: string;
}

export interface SpendingDemandListFilterViewProps {
  handleClickStatus: (value: string) => void;
  handleClickType: (value: string) => void;
  handleDateChange: (field: 'min' | 'max', value: string) => void;
  handleAmountChange: (field: 'min' | 'max', value: number | '') => void;
  handleProjectChange?: (projectId: string) => void;
  handleOpenProjectModal?: () => void;
  handleUserChange?: (userId: string) => void;
  handleOpenUserModal?: () => void;
  handleBoxIdChange?: (boxId: string) => void;
  handleReadableIdChange?: (readableId: string) => void;
  handleDescriptionChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  handleClearFilters?: () => void;
  handleCloseModal?: () => void;
  handleApplyFilters?: () => void;
  isModalOpen?: boolean;
  statusList: StatusItem[];
  typeList: TypeItem[];
  currentStatusSelected: string[] | undefined | null;
  currentTypeSelected: string[] | undefined | null;
  currentProjectSelected?: string | null;
  selectedProjectName?: string | null;
  currentUserSelected?: string | null;
  selectedUserName?: string | null;
  boxIdInput?: string;
  boxIdValidationStatus?: {
    isValid: boolean;
    isLoading: boolean;
    isError: boolean;
  };
  readableIdInput?: string;
  descriptionInput?: string;
  dateRange: { min?: string; max?: string };
  amountRange: { min?: number | ''; max?: number | '' };
  isExpanded?: boolean;
  onToggleExpanded?: () => void;
  /** When true, hides the User filter (user sees only their own demands) */
  forUser?: boolean;
}

export default function SpendingDemandListFilterView({
  handleClickStatus,
  handleClickType,
  handleDateChange,
  handleAmountChange,
  handleProjectChange,
  handleOpenProjectModal,
  handleUserChange,
  handleOpenUserModal,
  handleBoxIdChange,
  handleReadableIdChange,
  handleClearFilters,
  handleCloseModal,
  handleApplyFilters,
  isModalOpen = false,
  statusList,
  typeList,
  currentStatusSelected,
  currentTypeSelected,
  currentProjectSelected,
  selectedProjectName,
  currentUserSelected,
  selectedUserName,
  boxIdInput,
  boxIdValidationStatus,
  readableIdInput,
  descriptionInput,
  handleDescriptionChange,
  dateRange,
  amountRange,
  isExpanded = false,
  onToggleExpanded,
  forUser = false,
}: SpendingDemandListFilterViewProps) {
  const handleClearProject = () => {
    if (handleProjectChange) {
      handleProjectChange('');
    }
  };

  const handleClearUser = () => {
    if (handleUserChange) {
      handleUserChange('');
    }
  };
  
  return (
    <Box sx={{ 
      border: '1px solid #e0e0e0', 
      borderRadius: 0.5, 
      backgroundColor: '#fafafa',
      overflow: 'hidden',
      width: '100%',
      maxWidth: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Bouton compact */}
      <Box sx={{ 
        p: 0.5, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        cursor: 'pointer',
        minHeight: '32px',
        '&:hover': {
          backgroundColor: '#f0f0f0'
        }
      }} onClick={onToggleExpanded}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <FilterListIcon sx={{ fontSize: 14 }} />
          <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
            Filtres
          </Typography>
          {(currentStatusSelected && currentStatusSelected.length > 0) || 
           (currentTypeSelected && currentTypeSelected.length > 0) ||
           (currentProjectSelected) || (!forUser && currentUserSelected) ||
           (readableIdInput) ||
           (dateRange.min || dateRange.max) || (amountRange.min || amountRange.max) ? (
            <Box sx={{ 
              width: 16, 
              height: 16, 
              borderRadius: '50%', 
              backgroundColor: '#ffc107',
              ml: 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              color: '#000',
              fontWeight: 'bold',
              minWidth: '16px'
            }}>
              {[
                currentStatusSelected?.length || 0,
                currentTypeSelected?.length || 0,
                currentProjectSelected ? 1 : 0,
                !forUser && currentUserSelected ? 1 : 0,
                readableIdInput ? 1 : 0,
                (dateRange.min || dateRange.max) ? 1 : 0,
                (amountRange.min || amountRange.max) ? 1 : 0
              ].reduce((a, b) => a + b, 0)}
            </Box>
          ) : null}
        </Box>
        {isExpanded ? <ExpandMore sx={{ fontSize: 14, transform: 'rotate(180deg)' }} /> : <ExpandMore sx={{ fontSize: 14 }} />}
      </Box>

      {/* Contenu expandable */}
      <Collapse in={isExpanded}>
        <Box sx={{ p: 0.5, borderTop: '1px solid #e0e0e0' }}>
          {/* Bouton d'action */}
          {handleClearFilters && (
            <Box sx={{ mb: 0.5, display: 'flex', gap: 0.5 }}>
              <button
                onClick={handleClearFilters}
                style={{
                  width: '100px',
                  height: '18px',
                  fontSize: '10px',
                  backgroundColor: '#6c757d',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  padding: '0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px'
                }}
              >
                <ClearIcon sx={{ fontSize: 8 }} />
                Effacer
              </button>
            </Box>
          )}


          {/* Filtres par catégorie */}
          <Box sx={{ 
            mb: 0.5, 
            p: 0.5, 
            backgroundColor: '#f8f9fa', 
            borderRadius: '3px', 
            border: '1px solid #e0e0e0',
            maxHeight: '300px',
            overflowY: 'auto',
            width: '100%'
          }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start' }}>
              {/* Filtre par Statut */}
              <Box sx={{ flex: '0 1 auto', minWidth: '180px', maxWidth: '250px', alignSelf: 'flex-start' }}>
                <Typography variant="caption" sx={{ fontSize: '0.8rem', fontWeight: 500, mb: 0.5, display: 'block' }}>
                  Statut ({currentStatusSelected?.length || 0}/{statusList.length})
                </Typography>
                <FormGroup>
                  {statusList.map((status) => (
                    <FormControlLabel
                      key={status.value}
                      control={
                        <Checkbox
                          size="small"
                          checked={currentStatusSelected?.includes(status.value) || false}
                          onChange={() => handleClickStatus(status.value)}
                          sx={{ 
                            '& .MuiSvgIcon-root': { fontSize: 14 },
                            py: 0
                          }}
                        />
                      }
                      label={
                        <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                          {status.label}
                        </Typography>
                      }
                      sx={{ mb: 0, py: 0 }}
                    />
                  ))}
                </FormGroup>
              </Box>

              {/* Filtre par Type */}
              <Box sx={{ flex: '0 1 auto', minWidth: '180px', maxWidth: '250px', alignSelf: 'flex-start' }}>
                <Typography variant="caption" sx={{ fontSize: '0.8rem', fontWeight: 500, mb: 0.5, display: 'block' }}>
                  Type ({currentTypeSelected?.length || 0}/{typeList.length})
                </Typography>
                <FormGroup>
                  {typeList.map((type) => (
                    <FormControlLabel
                      key={type.value}
                      control={
                        <Checkbox
                          size="small"
                          checked={currentTypeSelected?.includes(type.value) || false}
                          onChange={() => handleClickType(type.value)}
                          sx={{ 
                            '& .MuiSvgIcon-root': { fontSize: 14 },
                            py: 0
                          }}
                        />
                      }
                      label={
                        <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                          {type.label}
                        </Typography>
                      }
                      sx={{ mb: 0, py: 0 }}
                    />
                  ))}
                </FormGroup>
              </Box>

              {/* Filtre par Projet */}
              <Box sx={{ flex: '0 1 auto', minWidth: '180px', maxWidth: '250px', alignSelf: 'flex-start' }}>
                <Typography variant="caption" sx={{ fontSize: '0.8rem', fontWeight: 500, mb: 0.5, display: 'block' }}>
                  Projet
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  {currentProjectSelected ? (
                    <button
                      onClick={handleClearProject}
                      style={{
                        minHeight: '32px',
                        fontSize: '0.8rem',
                        backgroundColor: '#e3f2fd',
                        color: '#1976d2',
                        border: '1px solid #1976d2',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        padding: '6px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px',
                        fontWeight: '500',
                        lineHeight: '1.2',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '200px'
                      }}
                      title={selectedProjectName || ''}
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {selectedProjectName}
                      </span>
                      <span style={{ fontSize: '12px', fontWeight: 'bold', flexShrink: 0 }}>✕</span>
                    </button>
                  ) : (
                    <button
                      onClick={handleOpenProjectModal}
                      style={{
                        height: '24px',
                        fontSize: '0.8rem',
                        backgroundColor: '#f5f5f5',
                        color: '#666',
                        border: '1px solid #ccc',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        padding: '0 8px',
                        fontWeight: '500'
                      }}
                    >
                      Choisir un projet
                    </button>
                  )}
                </Box>
              </Box>

              {/* Filtre par Utilisateur - masqué pour User (voit ses propres demandes) */}
              {!forUser && (
                <Box sx={{ flex: '0 1 auto', minWidth: '180px', maxWidth: '250px', alignSelf: 'flex-start' }}>
                  <Typography variant="caption" sx={{ fontSize: '0.8rem', fontWeight: 500, mb: 0.5, display: 'block' }}>
                    Utilisateur
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    {currentUserSelected ? (
                      <button
                        onClick={handleClearUser}
                        style={{
                          minHeight: '32px',
                          fontSize: '0.8rem',
                          backgroundColor: '#e8f5e8',
                          color: '#2e7d32',
                          border: '1px solid #2e7d32',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          padding: '6px 8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                          fontWeight: '500',
                          lineHeight: '1.2',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '200px'
                        }}
                        title={selectedUserName || ''}
                      >
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {selectedUserName}
                        </span>
                        <span style={{ fontSize: '12px', fontWeight: 'bold', flexShrink: 0 }}>✕</span>
                      </button>
                    ) : (
                      <button
                        onClick={handleOpenUserModal}
                        style={{
                          height: '24px',
                          fontSize: '0.8rem',
                          backgroundColor: '#f5f5f5',
                          color: '#666',
                          border: '1px solid #ccc',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          padding: '0 8px',
                          fontWeight: '500'
                        }}
                      >
                        Choisir un utilisateur
                      </button>
                    )}
                  </Box>
                </Box>
              )}

              {/* Filtre par Box ID */}
              <Box sx={{ flex: '0 1 auto', minWidth: '180px', maxWidth: '250px', alignSelf: 'flex-start', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="caption" sx={{ fontSize: '0.8rem', fontWeight: 500, mb: 0.5, display: 'block' }}>
                  Box ID
                </Typography>
                <Box sx={{ position: 'relative', width: '100%', display: 'block' }}>
                  <input
                    type="text"
                    value={boxIdInput || ''}
                    onChange={(e) => handleBoxIdChange && handleBoxIdChange(e.target.value)}
                    placeholder="Entrez le Box ID"
                    style={{
                      height: '36px',
                      fontSize: '1rem',
                      padding: '8px 40px 8px 12px',
                      border: boxIdValidationStatus?.isError && !boxIdValidationStatus?.isLoading && boxIdInput && !boxIdValidationStatus?.isValid
                        ? '1px solid #f44336'
                        : '1px solid #ccc',
                      borderRadius: '4px',
                      backgroundColor: '#fff',
                      outline: 'none',
                      fontFamily: 'inherit',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                  />
                  {boxIdInput && boxIdValidationStatus && (
                    <Box
                      sx={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        display: 'flex',
                        alignItems: 'center',
                        pointerEvents: 'none',
                        zIndex: 1
                      }}
                    >
                      {boxIdValidationStatus.isLoading ? (
                        <CircularProgress size={20} />
                      ) : boxIdValidationStatus.isValid ? (
                        <CheckValidationIcon size={20} color="#4caf50" />
                      ) : boxIdValidationStatus.isError ? (
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#f44336"
                          strokeWidth="2"
                        >
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="15" y1="9" x2="9" y2="15"></line>
                          <line x1="9" y1="9" x2="15" y2="15"></line>
                        </svg>
                      ) : null}
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Filtre par ID */}
              <Box sx={{ flex: '0 1 auto', minWidth: '180px', maxWidth: '250px', alignSelf: 'flex-start', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="caption" sx={{ fontSize: '0.8rem', fontWeight: 500, mb: 0.5, display: 'block' }}>
                  ID
                </Typography>
                <input
                  type="text"
                  value={readableIdInput || ''}
                  onChange={(e) => handleReadableIdChange && handleReadableIdChange(e.target.value)}
                  placeholder="Entrez l'ID"
                  style={{
                    height: '36px',
                    fontSize: '1rem',
                    padding: '8px 12px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    backgroundColor: '#fff',
                    outline: 'none',
                    fontFamily: 'inherit',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                />
              </Box>

              {/* Filtre par Description - masqué pour User (route user sans support descSearch) */}
              {!forUser && (
                <Box sx={{ flex: '0 1 auto', minWidth: '180px', maxWidth: '250px', alignSelf: 'flex-start', display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="caption" sx={{ fontSize: '0.8rem', fontWeight: 500, mb: 0.5, display: 'block' }}>
                    Description
                  </Typography>
                  <input
                    type="text"
                    value={descriptionInput || ''}
                    onChange={(e) => handleDescriptionChange && handleDescriptionChange(e)}
                    placeholder="Rechercher dans la description"
                    style={{
                      height: '36px',
                      fontSize: '1rem',
                      padding: '8px 12px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      backgroundColor: '#fff',
                      outline: 'none',
                      fontFamily: 'inherit',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                  />
                </Box>
              )}

              {/* Filtre par Date */}
              <Box sx={{ flex: '0 1 auto', minWidth: '180px', maxWidth: '250px', alignSelf: 'flex-start' }}>
                <Typography variant="caption" sx={{ fontSize: '0.8rem', fontWeight: 500, mb: 0.5, display: 'block' }}>
                  Date
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    <label style={{ fontSize: '0.8rem', fontWeight: 500, color: '#333' }}>
                      Date min
                    </label>
                    <input
                      type="date"
                      value={dateRange.min || ''}
                      onChange={(e) => handleDateChange('min', e.target.value)}
                      style={{
                        height: '36px',
                        fontSize: '1rem',
                        padding: '8px 12px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        backgroundColor: '#fff',
                        outline: 'none',
                        fontFamily: 'inherit'
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    <label style={{ fontSize: '0.8rem', fontWeight: 500, color: '#333' }}>
                      Date max
                    </label>
                    <input
                      type="date"
                      value={dateRange.max || ''}
                      onChange={(e) => handleDateChange('max', e.target.value)}
                      style={{
                        height: '36px',
                        fontSize: '1rem',
                        padding: '8px 12px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        backgroundColor: '#fff',
                        outline: 'none',
                        fontFamily: 'inherit'
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Filtre par Montant */}
              <Box sx={{ flex: '0 1 auto', minWidth: '180px', maxWidth: '250px', alignSelf: 'flex-start' }}>
                <Typography variant="caption" sx={{ fontSize: '0.8rem', fontWeight: 500, mb: 0.5, display: 'block' }}>
                  Montant (FCFA)
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    <label style={{ fontSize: '0.8rem', fontWeight: 500, color: '#333' }}>
                      Min
                    </label>
                    <input
                      type="number"
                      value={amountRange.min === '' ? '' : amountRange.min}
                      onChange={(e) => handleAmountChange('min', e.target.value === '' ? '' : Number(e.target.value))}
                      min="0"
                      step="0.01"
                      placeholder="0"
                      style={{
                        height: '36px',
                        fontSize: '1rem',
                        padding: '8px 12px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        backgroundColor: '#fff',
                        outline: 'none',
                        fontFamily: 'inherit'
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    <label style={{ fontSize: '0.8rem', fontWeight: 500, color: '#333' }}>
                      Max
                    </label>
                    <input
                      type="number"
                      value={amountRange.max === '' ? '' : amountRange.max}
                      onChange={(e) => handleAmountChange('max', e.target.value === '' ? '' : Number(e.target.value))}
                      min="0"
                      step="0.01"
                      placeholder="∞"
                      style={{
                        height: '36px',
                        fontSize: '1rem',
                        padding: '8px 12px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        backgroundColor: '#fff',
                        outline: 'none',
                        fontFamily: 'inherit'
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

        </Box>
      </Collapse>
    </Box>
  );
}